/**
 * One reader for "what went wrong" across every transport shape the API can
 * hand back (PLX §6).
 *
 * The backend answers with `{ error }` on auth routes, `{ message }` on the
 * business routes, and occasionally a bare string — and an endpoint declared
 * with a text response handler surfaces a JSON body as an unparsed string.
 * Callers should never have to know which of those they got.
 */

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" ? (value as Record<string, unknown>) : null;
}

function firstString(source: Record<string, unknown>, keys: string[]): string | undefined {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === "string" && value.trim()) return value;
  }
  return undefined;
}

/** A JSON body that arrived as text still carries the real message. */
function parseMaybeJson(text: string): Record<string, unknown> | null {
  const trimmed = text.trim();
  if (!trimmed.startsWith("{") && !trimmed.startsWith("[")) return null;
  try {
    return asRecord(JSON.parse(trimmed));
  } catch {
    return null;
  }
}

const MESSAGE_KEYS = ["error", "message", "detail", "title"];

/**
 * The server's own words for a failure, or `fallback` when it offered none.
 * Never returns an empty string, so it is always safe to render.
 */
export function extractApiError(error: unknown, fallback: string): string {
  const envelope = asRecord(error);
  const body = envelope?.data ?? asRecord(envelope?.response)?.data ?? error;

  if (typeof body === "string") {
    const parsed = parseMaybeJson(body);
    const message = parsed ? firstString(parsed, MESSAGE_KEYS) : undefined;
    return message ?? (body.trim() || fallback);
  }

  const record = asRecord(body);
  if (record) {
    const message = firstString(record, MESSAGE_KEYS);
    if (message) return message;
    const nested = asRecord(record.data);
    if (nested) {
      const nestedMessage = firstString(nested, MESSAGE_KEYS);
      if (nestedMessage) return nestedMessage;
    }
  }

  if (envelope) {
    const envelopeMessage = firstString(envelope, ["message"]);
    if (envelopeMessage) return envelopeMessage;
  }

  return fallback;
}
